// src/new/components/monitor/TriageChat/useVedaChatSocket.ts
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { vedaWebSocketUrl } from './vedaSession';
import {
  buildUserMessageEnvelope,
  buildStopEnvelope,
  parseServerEnvelope,
  ServerEvent,
} from './vedaProtocol';
import { SubagentState } from './triageBackend';

export type ChatRole = 'user' | 'assistant' | 'tool';

export interface ChatMessage {
  id: string;
  role: ChatRole;
  text: string;
  streaming?: boolean;
  createdAt?: number;
  /** Non-empty only for an assistant turn that had subagent activity — same
   *  shape produced by `TriageBackend.getHistory` (see triageBackend.ts) so a
   *  turn rendered from history and a turn rendered live via express-code's
   *  SSE events use one bubble shape. veda-ai never populates this. */
  subagents?: Record<string, SubagentState>;
  /** express-code only: per-part text for THIS assistant message, keyed by
   *  OpenCode part id, with `expressPartOrder` preserving arrival order. The
   *  rendered `text` is the ordered join. Lets one message's multiple parts
   *  accumulate independently (deltas append, part.updated snaps) without
   *  clobbering, while distinct messageIds stay separate bubbles. veda-ai
   *  never populates these (it has no part concept). */
  expressParts?: Record<string, string>;
  expressPartOrder?: string[];
}

export interface VedaChatSocketState {
  connected: boolean;
  streaming: boolean;
  error: string | null;
}

interface UseVedaChatSocketArgs {
  baseUrl: string;
  sessionId: string | null;
  token: string;
  onEvent: (e: ServerEvent) => void;
  /** Called when an established connection drops and is re-established (NOT on
   *  the first connect). veda-ai rebuilds the agent's in-memory context from
   *  persisted history on every new connection, so on reconnect the caller
   *  should reload session history to keep the UI (and the user's mental model)
   *  aligned with what the agent now remembers. */
  onReconnect?: () => void;
}

const PING_INTERVAL_MS = 30_000;
const MAX_RECONNECTS = 8;
// A connection open at least this long is considered healthy: a later drop is a
// fresh fault, so we reset the reconnect budget rather than counting it against
// the original burst. Prevents a flapping connection from exhausting the budget
// instantly while still capping true storms.
const STABLE_OPEN_MS = 15_000;
// On unmount we DON'T close the socket immediately. A transient remount (parent
// re-render that swaps the subtree, a key flip, a route blip) would otherwise
// tear down the live connection mid-stream — which aborts the in-flight agent
// turn server-side (it never persists) and makes the next question land on a
// brand-new connection with no memory of it. Holding the socket open for a
// grace period lets a re-mount re-adopt the SAME connection, so the whole
// conversation stays on one server-side agent_state and context is preserved.
const UNMOUNT_GRACE_MS = 2_000;

interface LiveSocket {
  ws: WebSocket;
  graceTimer: ReturnType<typeof setTimeout> | null;
}

// Live sockets keyed by sessionId, surviving transient component remounts. This
// mirrors veda-ui's singleton WebSocket service (which lives outside React), the
// key reason its follow-up context never breaks: the connection is not coupled
// to a component's mount lifecycle.
const liveSockets = new Map<string, LiveSocket>();

/**
 * Browser client for veda-ai's WebSocket V2 protocol. Auth is via `?token=`
 * query param (browsers can't set Authorization on a WS). Sends text user
 * messages, forwards parsed server events to `onEvent`, heartbeats with PING,
 * and reconnects with backoff. The socket is held in a module-level registry so
 * it survives transient remounts, and on a genuine reconnect it self-heals by
 * reloading history (see `onReconnect`).
 */
export function useVedaChatSocket({
  baseUrl,
  sessionId,
  token,
  onEvent,
  onReconnect,
}: UseVedaChatSocketArgs) {
  const wsRef = useRef<WebSocket | null>(null);
  const pingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectsRef = useRef(0);
  const reqCounterRef = useRef(0);
  const orderRef = useRef(0);
  const onEventRef = useRef(onEvent);
  const onReconnectRef = useRef(onReconnect);
  const unmountedRef = useRef(false);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Has a connection ever opened for this hook instance? Used to distinguish the
  // first connect (seed flow) from a reconnect (reload-history flow).
  const hasOpenedRef = useRef(false);
  const openedAtRef = useRef(0);
  // Outgoing messages queued while the socket is not OPEN (e.g. during a
  // reconnect). Flushed on the next `onopen` so a follow-up is never dropped
  // and always lands on the same session.
  const pendingRef = useRef<string[]>([]);
  onEventRef.current = onEvent;
  onReconnectRef.current = onReconnect;

  const [state, setState] = useState<VedaChatSocketState>({
    connected: false,
    streaming: false,
    error: null,
  });

  const clearPing = () => {
    if (pingRef.current) {
      clearInterval(pingRef.current);
      pingRef.current = null;
    }
  };

  const startPing = useCallback((ws: WebSocket) => {
    clearPing();
    pingRef.current = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send('PING');
    }, PING_INTERVAL_MS);
  }, []);

  const flushPending = useCallback(() => {
    if (pendingRef.current.length === 0) return;
    const queued = pendingRef.current;
    pendingRef.current = [];
    queued.forEach((t) => sendRawRef.current(t));
  }, []);

  // (Re)bind handlers to the current hook closures. Called both for a freshly
  // created socket and when adopting a surviving one from the registry, so the
  // adopted socket routes events to THIS mount's onEvent/onReconnect.
  const bindHandlers = useCallback(
    (ws: WebSocket) => {
      ws.onopen = () => {
        const wasReconnect = hasOpenedRef.current;
        hasOpenedRef.current = true;
        openedAtRef.current = Date.now();
        reconnectsRef.current = 0;
        setState((s) => ({ ...s, connected: true, error: null }));
        startPing(ws);
        flushPending();
        // A reconnect means the server spun up a fresh agent_state from
        // persisted history — reload it so UI + agent memory stay aligned.
        if (wasReconnect) onReconnectRef.current?.();
      };

      ws.onmessage = (ev) => {
        const parsed = parseServerEnvelope(typeof ev.data === 'string' ? ev.data : '');
        if (parsed.kind === 'text' || parsed.kind === 'tool') {
          setState((s) => ({ ...s, streaming: !parsed.done }));
        }
        if (parsed.kind === 'error') {
          setState((s) => ({ ...s, streaming: false, error: parsed.message }));
        }
        onEventRef.current(parsed);
      };

      ws.onclose = () => {
        // Only react if this is still the socket we care about.
        if (wsRef.current !== ws) return;
        setState((s) => ({ ...s, connected: false, streaming: false }));
        clearPing();
        if (sessionId && liveSockets.get(sessionId)?.ws === ws) {
          liveSockets.delete(sessionId);
        }
        if (unmountedRef.current) return;
        // A connection that was healthy for a while earns a fresh budget.
        if (openedAtRef.current && Date.now() - openedAtRef.current > STABLE_OPEN_MS) {
          reconnectsRef.current = 0;
        }
        if (reconnectsRef.current < MAX_RECONNECTS) {
          const delay = Math.min(1000 * 2 ** reconnectsRef.current, 10_000);
          reconnectsRef.current += 1;
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            if (!unmountedRef.current) connectRef.current();
          }, delay);
        }
      };

      ws.onerror = () => {
        setState((s) => ({ ...s, error: s.error ?? 'WebSocket connection error' }));
      };
    },
    [sessionId, startPing, flushPending],
  );

  const connect = useCallback(() => {
    if (!sessionId || unmountedRef.current) return;

    // Re-adopt a surviving socket for this session (transient remount) instead
    // of opening a new connection — keeps the conversation on one agent_state.
    const existing = liveSockets.get(sessionId);
    if (
      existing &&
      (existing.ws.readyState === WebSocket.OPEN || existing.ws.readyState === WebSocket.CONNECTING)
    ) {
      if (existing.graceTimer) {
        clearTimeout(existing.graceTimer);
        existing.graceTimer = null;
      }
      const ws = existing.ws;
      wsRef.current = ws;
      bindHandlers(ws);
      if (ws.readyState === WebSocket.OPEN) {
        // Already open: adoption is not a reconnect (context is intact), so set
        // hasOpened first to avoid firing onReconnect, then mark connected.
        hasOpenedRef.current = true;
        openedAtRef.current = openedAtRef.current || Date.now();
        setState((s) => ({ ...s, connected: true, error: null }));
        startPing(ws);
        flushPending();
      }
      return;
    }

    // Pass the token as a query param — browsers can't set an Authorization
    // header on a WebSocket. Wrap construction so a malformed URL/token can
    // never crash the host app.
    const url = `${vedaWebSocketUrl(baseUrl, sessionId)}?token=${encodeURIComponent(token)}`;
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      setState((s) => ({
        ...s,
        connected: false,
        error: err instanceof Error ? err.message : 'Failed to open chat connection',
      }));
      return;
    }
    wsRef.current = ws;
    liveSockets.set(sessionId, { ws, graceTimer: null });
    bindHandlers(ws);
  }, [baseUrl, sessionId, token, bindHandlers, startPing, flushPending]);
  const connectRef = useRef(connect);
  connectRef.current = connect;

  useEffect(() => {
    unmountedRef.current = false;
    connect();
    return () => {
      unmountedRef.current = true;
      clearPing();
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      const ws = wsRef.current;
      wsRef.current = null;
      // Don't close immediately: hold the socket for a grace period so a
      // transient remount can re-adopt it. Only truly close if no re-mount
      // claimed it within the window.
      if (ws && sessionId) {
        const entry = liveSockets.get(sessionId);
        if (entry && entry.ws === ws) {
          if (entry.graceTimer) clearTimeout(entry.graceTimer);
          entry.graceTimer = setTimeout(() => {
            // If still owned by this stale entry, close it for real.
            if (liveSockets.get(sessionId)?.ws === ws) {
              liveSockets.delete(sessionId);
            }
            try {
              ws.close();
            } catch {
              /* already closing */
            }
          }, UNMOUNT_GRACE_MS);
        }
      } else if (ws) {
        try {
          ws.close();
        } catch {
          /* already closing */
        }
      }
    };
  }, [connect, sessionId]);

  // Low-level send: serializes + writes the envelope (assumes the socket is
  // OPEN). Kept in a ref so handlers (bound before this) can flush the queue.
  const sendRaw = useCallback(
    (text: string) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN || !sessionId) return false;
      reqCounterRef.current += 1;
      const env = buildUserMessageEnvelope(
        sessionId,
        text,
        `req-${reqCounterRef.current}`,
        () =>
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${reqCounterRef.current}`,
        orderRef.current++,
      );
      ws.send(JSON.stringify(env));
      return true;
    },
    [sessionId],
  );
  const sendRawRef = useRef(sendRaw);
  sendRawRef.current = sendRaw;

  const sendUserMessage = useCallback((text: string) => {
    // Optimistically accept the message: send now if connected, otherwise
    // queue it to flush on reconnect. Either way the caller renders the bubble
    // and the message lands on the same session (preserving context).
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      sendRawRef.current(text);
    } else {
      pendingRef.current.push(text);
    }
    setState((s) => ({ ...s, streaming: true, error: null }));
    return true;
  }, []);

  // Ask the server to stop the in-flight generation. Also drops any queued
  // sends and clears the local streaming state so the UI returns to idle.
  const stopGeneration = useCallback(() => {
    pendingRef.current = [];
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      reqCounterRef.current += 1;
      ws.send(JSON.stringify(buildStopEnvelope(`req-${reqCounterRef.current}`)));
    }
    setState((s) => ({ ...s, streaming: false }));
  }, []);

  return useMemo(
    () => ({ ...state, sendUserMessage, stopGeneration }),
    [state, sendUserMessage, stopGeneration],
  );
}
